import tactrlLogo from '@/assets/images/MainLogo.svg'
import { ZIndexRange } from '@/types/style'
import { useCompany, usePushHistory } from '@/utils/hooks'
import type { AppPath } from '@/utils/hooks/useAppPathTranslations'
import { useAppPathTranslations } from '@/utils/hooks/useAppPathTranslations'
import { useBreadcrumbs } from '@/utils/hooks/useBreadcrumbs/useBreadcrumbs'
import { requestScrollRestore } from '@/utils/hooks/use-scroll-restoration'
import { overloadColor } from '@/utils/style'
import type { Crumb } from '@ur-solutions/react-components'
import { Breadcrumbs } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import capitalize from 'lodash/capitalize'
import React, { useCallback } from 'react'
import { isBrowser } from 'react-device-detect'
import styled from 'styled-components'
import { CompanyLogo } from '../sidebar/CompanyLogo'
import { HeaderMenu } from './components'

const BrowserWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: ${props => props.theme.layout.headerHeight};
  padding: 32px 52px 0;
`
const MobileWrapper = styled(BrowserWrapper)`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  z-index: ${ZIndexRange.Header};
  height: ${props => props.theme.layout.headerHeightMobile};
  padding: 1.2em;

  background-color: white;
  box-shadow: 0 1px 18px 0 #00000009;

  div.logo-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      margin-left: 0.8em;
      font-size: 1.1em;
      font-weight: 700;
      color: ${props => props.theme.colors.primary};
    }
  }
`
const TitleImage = styled.img`
  height: 30px;
`
const RouteBreadcrumbs = styled(Breadcrumbs.Route)`
  max-width: 96vw;
  overflow: hidden;
  color: ${props => props.theme.colors.gray4};
  font-weight: 600;

  a {
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  ${props => props.theme.media.largePad} {
    display: flex;
    overflow-x: auto;
  }

  ${props => props.theme.media.mobile} {
    display: flex;
    overflow-x: auto;
  }
`

export const Header: React.FC = () => {
  const translations = useTranslate({
    home: 'common.home',
  })

  const pushHistory = usePushHistory()

  const company = useCompany()
  const { overrides, inserts } = useBreadcrumbs()

  const getAppPathTranslations = useAppPathTranslations()

  const overrideCrumb = useCallback(
    (path: string, index: number, total: number): Partial<Crumb> | null => {
      const text =
        path in overrides
          ? overrides[path]
          : getAppPathTranslations(path as AppPath) ?? capitalize(path)

      if (text === null) return null

      return {
        text,
        color: index === total - 1 ? overloadColor('primary') : undefined,
        // Going up the trail is a way back to a page the user has already been
        // on, so it should land where they left it.
        onClick: requestScrollRestore,
      }
    },
    [getAppPathTranslations, overrides]
  )

  if (isBrowser)
    return (
      <BrowserWrapper>
        <RouteBreadcrumbs
          mainPageTitle={translations.home}
          overrideCrumb={overrideCrumb}
          inserts={inserts}
          iconProps={{
            size: '0.6em',
          }}
        />

        <HeaderMenu />
      </BrowserWrapper>
    )

  return (
    <>
      <MobileWrapper>
        {!!company.logo && company.useCompanyLogo ? (
          <CompanyLogo logo={company.logo!} />
        ) : (
          <div className="logo-wrapper">
            <a
              href="/"
              onClick={evt => {
                evt.preventDefault()
                pushHistory('/', evt)
              }}
            >
              <TitleImage src={tactrlLogo} />
            </a>
          </div>
        )}
        <HeaderMenu />
      </MobileWrapper>

      <RouteBreadcrumbs
        className="breadcrumbs"
        mainPageTitle={translations.home}
        overrideCrumb={overrideCrumb}
        inserts={inserts}
        iconProps={{
          translateY: '-1px',
          size: '0.6em',
        }}
        iconMargins="8px"
      />
    </>
  )
}
